import React, { useState, useEffect } from "react";
import SlopBeforeLoading from "./SlopBeforeLoading";
import SlopAfterLoading from "./SlopAfterLoading";
import {
  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Button,
  Tooltip,
} from "@mui/material";
import {
  History as HistoryIcon,
  DeleteSweep as DeleteIcon,
  SettingsBackupRestore,
  Assignment,
} from "@mui/icons-material";
import { renderSlopV2 } from "../../print-v2";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useUserContext } from "../../Contexts/UserContext";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useSettings } from "../../Contexts/SettingsContext";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";

const Slop = () => {
  const { t } = useTranslation("slop");
  const theme = useTheme();
  const {
    selectedTripId,
    selectedCargoIndex,
    currentVoyage,
    isVoyageReadOnly,
    updateTripCargo,
    addLog,
  } = useVoyageContextV2();

  const { user } = useUserContext();
  const { dateFormat } = useSettings();
  const { companyLogo } = useCompanyContext();

  const [reportType, setReportType] = useState("before"); // 'before' eller 'after'
  const [loading, setLoading] = useState(true);
  const date = dayjs().format(dateFormat || "YYYY-MM-DD");

  useEffect(() => {
    if (currentVoyage) setLoading(false);
  }, [currentVoyage]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  const handleToggle = (event, newType) => {
    if (newType !== null) setReportType(newType);
  };

  const handleDeleteSlopData = () => {
    if (isVoyageReadOnly) return;
    if (
      !window.confirm(
        "Are you sure you want to delete all SLOP data for this cargo?"
      )
    )
      return;

    const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);
    if (!selectedTrip) return;
    const selectedCargo = getSelectedCargo(
      currentVoyage,
      selectedTripId,
      selectedCargoIndex
    );
    if (!selectedCargo?.cargoId) return;

    updateTripCargo(
      selectedCargo.cargoId,
      { slopData: {} },
      { summary: "Cleared slop data" }
    );
    addLog({
      user: user?.username || user?.userName || "Unknown",
      activity: "Deleted SLOP",
      details: "User deleted SLOP data",
      tripId: selectedTrip.tripId || selectedTripId,
      cargoId: selectedCargo.cargoId,
    });
  };

  const handleSaveButton = async (type) => {
    if (isVoyageReadOnly) return currentVoyage;
    const measurements = [
      "UTI Ullage",
      "Corr Ullage",
      "GOV (m3)",
      "Last Cargo",
      "UTI Interface",
      "Corr Interface",
      "Water GOV",
    ];

    const getFieldData = (measurement) => {
      const key = measurement.toLowerCase().replace(/\s+/g, "-");
      return {
        p: document.getElementById(`${key}-p`)?.value || "",
        s: document.getElementById(`${key}-s`)?.value || "",
        cargo: document.getElementById(`${key}-cargo`)?.value || "",
        remarks: document.getElementById(`${key}-remarks`)?.value || "",
      };
    };

    const newSlopData = {
      measurements: measurements.reduce((acc, m) => {
        acc[m.toLowerCase().replace(/\s+/g, "-")] = getFieldData(m);
        return acc;
      }, {}),
      remarks: document.getElementById(`remarks${type}`)?.value || "",
    };

    const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);
    if (!selectedTrip) return currentVoyage;
    const selectedCargo = getSelectedCargo(
      currentVoyage,
      selectedTripId,
      selectedCargoIndex
    );
    if (!selectedCargo?.cargoId) return currentVoyage;

    const updatedSlopData = {
      ...(selectedCargo.slopData || {}),
      [type]: newSlopData,
    };

    const updatedVoyage = {
      ...currentVoyage,
      trips: {
        ...(currentVoyage?.trips || {}),
        [selectedTripId]: {
          ...selectedTrip,
          cargos: (selectedTrip.cargos || []).map((cargo, cargoIndex) =>
            cargoIndex === selectedCargoIndex
              ? { ...cargo, slopData: updatedSlopData }
              : cargo
          ),
        },
      },
    };

    await updateTripCargo(
      selectedCargo.cargoId,
      { slopData: updatedSlopData },
      { summary: `Updated slop report ${type}` }
    );
    return new Promise((resolve) =>
      setTimeout(() => resolve(updatedVoyage), 300)
    );
  };

  const handlePrintButton = async (type) => {
    const updatedVoyage = isVoyageReadOnly
      ? currentVoyage
      : await handleSaveButton(type);
    renderSlopV2({
      currentVoyage: updatedVoyage,
      selectedTripId,
      selectedCargoIndex,
      reportType: type,
      companyLogo,
      date,
      t,
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pb: 5 }}>
      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="900"
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            <Assignment sx={{ fontSize: 35, color: "primary.main" }} />
            {t("title")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Record and manage Slop Tank measurements for environmental
            compliance.
          </Typography>
        </Box>

        <Tooltip title="Delete all Slop data">
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSlopData}
            disabled={isVoyageReadOnly}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Clear Data
          </Button>
        </Tooltip>
      </Stack>

      {/* Mode Selector (Toggle Tabs) */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 4,
          display: "inline-flex",
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <ToggleButtonGroup
          value={reportType}
          exclusive
          onChange={handleToggle}
          sx={{
            "& .MuiToggleButton-root": {
              px: 4,
              py: 1,
              borderRadius: 2,
              border: "none",
              fontWeight: 800,
              textTransform: "none",
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              },
            },
          }}
        >
          <ToggleButton value="before">
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryIcon fontSize="small" />
              {t("before")}
            </Stack>
          </ToggleButton>
          <ToggleButton value="after">
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsBackupRestore fontSize="small" />
              {t("after")}
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ position: "relative" }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            minHeight: 400,
          }}
        >
          {reportType === "before" ? (
            <SlopBeforeLoading
              currentVoyage={currentVoyage}
              selectedTripId={selectedTripId}
              selectedCargoIndex={selectedCargoIndex}
              handlePrintButton={() => handlePrintButton("before")}
              handleSaveButton={() => handleSaveButton("before")}
              handleDeleteSlopData={handleDeleteSlopData}
              readOnly={isVoyageReadOnly}
              date={date}
            />
          ) : (
            <SlopAfterLoading
              currentVoyage={currentVoyage}
              selectedTripId={selectedTripId}
              selectedCargoIndex={selectedCargoIndex}
              handlePrintButton={() => handlePrintButton("after")}
              handleSaveButton={() => handleSaveButton("after")}
              handleDeleteSlopData={handleDeleteSlopData}
              readOnly={isVoyageReadOnly}
              date={date}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Slop;
